import { useMemo } from 'react'
import { useAccount, useNetwork } from 'wagmi'
import { useRequest } from 'ahooks'
import { BigNumberish } from 'ethers'
import { useProviderOrSigner } from './useProviderOrSigner'
import { getFarmContract } from '@/utils/contract'
import {
  claimCall,
  earnedCall,
  poolLengthCall,
  poolsCall,
  stakeCall,
  unstakeCall,
  usersCall,
} from '@/web3/contractCalls/farm'

export function useFarmContract(withSignerIfPossible = true) {
  const { chain } = useNetwork()
  const providerOrSigner = useProviderOrSigner(withSignerIfPossible)

  return useMemo(() => getFarmContract(providerOrSigner, chain?.id), [chain?.id, providerOrSigner])
}

export const usePoolsCount = () => {
  const contract = useFarmContract()

  return useRequest(
    async () => {
      return poolLengthCall({ contract })
    },
    { refreshDeps: [contract], ready: !!contract },
  )
}

export const usePoolInfo = (
  poolIds?: number[],
  options?: {
    pollingInterval?: number
  },
) => {
  const contract = useFarmContract()

  return useRequest(
    async () => {
      if (!poolIds) return
      const requests = poolIds.map(async (id) => {
        const resp = await poolsCall({ contract, poolId: id })
        return { ...resp, id }
      })
      return Promise.all(requests)
    },
    {
      refreshDeps: [contract],
      ready: !!contract && !!poolIds && poolIds.length > 0,
      pollingInterval: options?.pollingInterval,
      loadingDelay: 300,
    },
  )
}

export const useUserParticipation = (poolId: number) => {
  const contract = useFarmContract()

  const { address: accountAddress } = useAccount()

  return useRequest(
    async () => {
      if (typeof poolId !== 'number' && !accountAddress) {
        return Promise.reject(new Error('Invalid args'))
      }

      return usersCall({
        contract,
        poolId,
        accountAddress,
      })
    },
    {
      refreshDeps: [contract, accountAddress],
      ready: typeof poolId === 'number' && !!accountAddress,
    },
  )
}

export const useStake = (
  poolId: number,
  amount: BigNumberish,
  options?: {
    onSuccess?: () => void
    onError?: () => void
  },
) => {
  const contract = useFarmContract()

  const isArgsInvalid = typeof poolId !== 'number' && !amount

  return useRequest(
    async () => {
      if (isArgsInvalid) {
        return Promise.reject(new Error('Invalid args'))
      }

      return stakeCall({
        contract,
        poolId,
        amount,
      })
    },
    {
      manual: true,
      ready: !isArgsInvalid,
      onSuccess: () => {
        options?.onSuccess?.()
      },
      onError: () => {
        options?.onError?.()
      },
    },
  )
}

export const useClaim = (
  poolId: number,
  options?: {
    onSuccess?: () => void
    onError?: () => void
  },
) => {
  const contract = useFarmContract()

  const isArgsInvalid = typeof poolId !== 'number'

  return useRequest(
    async () => {
      if (isArgsInvalid) {
        return Promise.reject(new Error('Invalid args'))
      }

      return claimCall({
        contract,
        poolId,
      })
    },
    {
      manual: true,
      ready: !isArgsInvalid,
      onSuccess: () => {
        options?.onSuccess?.()
      },
      onError: () => {
        options?.onError?.()
      },
    },
  )
}

export const useUnstake = (
  poolId: number,
  amount: BigNumberish,
  options?: {
    onSuccess?: () => void
    onError?: () => void
  },
) => {
  const contract = useFarmContract()

  const isArgsInvalid = typeof poolId !== 'number' && !amount

  return useRequest(
    async () => {
      if (isArgsInvalid) {
        return Promise.reject(new Error('Invalid args'))
      }

      return unstakeCall({
        contract,
        poolId,
        amount,
      })
    },
    {
      manual: true,
      ready: !isArgsInvalid,
      onSuccess: () => {
        options?.onSuccess?.()
      },
      onError: () => {
        options?.onError?.()
      },
    },
  )
}

export const useUserEarned = (
  poolId: number,
  options?: {
    pollingInterval?: number
  },
) => {
  const contract = useFarmContract()

  const { address: accountAddress } = useAccount()

  return useRequest(
    async () => {
      if (typeof poolId !== 'number' && !accountAddress) {
        return Promise.reject(new Error('Invalid args'))
      }

      return earnedCall({
        contract,
        poolId,
        accountAddress,
      })
    },
    {
      refreshDeps: [contract, accountAddress],
      ready: typeof poolId === 'number' && !!accountAddress,
      pollingInterval: options?.pollingInterval,
      loadingDelay: 300,
    },
  )
}

export const useUserStakedPoolId = (poolIds: number[]) => {
  const contract = useFarmContract()

  const { address: accountAddress } = useAccount()

  return useRequest(
    async () => {
      if (!!poolIds && !accountAddress) {
        return Promise.reject(new Error('Invalid args'))
      }

      const request = poolIds.map(async (poolId) => {
        const resp = await usersCall({
          contract,
          poolId,
          accountAddress,
        })

        return { ...resp, poolId }
      })

      const resp = await Promise.all(request)

      return resp
        .filter((participation) => participation.stakedAmount.gt(0))
        .map((participation) => participation.poolId)
    },
    { refreshDeps: [contract, accountAddress], ready: !!poolIds && !!accountAddress },
  )
}
